import React, { FC, PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';

import ErrorBoundary from 'Src/components/ErrorBoundary';
import { setDefaultShouldMaintainClass } from 'Src/feature/flat';
import { useResizeObserver } from 'Src/feature/resize/useResizeObserver';
import APIDate, { APIShortDate } from 'Src/models/APIDate';
import HMS24 from 'Src/models/HMS24';
import TimePeriod from 'Src/models/TimePeriod';

// default setup for all classes we don't want flattened when used in the context of a form
setDefaultShouldMaintainClass(
  (obj): boolean =>
    obj instanceof DateTime ||
    DateTime.isDateTime(obj) ||
    obj instanceof APIDate ||
    obj instanceof APIShortDate ||
    obj instanceof HMS24 ||
    obj instanceof TimePeriod,
);

const sectionId = 'AppContainer';

const is = {
  android: ~navigator.userAgent.indexOf('Android'),
};

// const pixels = {
//   android: {
//     locationBar: {
//       // The stock Android browser has a location bar height of 56 pixels, but
//       // this very likely could be broken in other Android browsers.
//       height: 56,
//     },
//   },
// };

interface AppContainerProps {
  title: string;
  rootId: string;
}

const AppContainer: FC<PropsWithChildren<AppContainerProps>> = ({ rootId, title, children }) => {
  const rootElement = useMemo(() => document.getElementById(rootId), [rootId]);

  const ref = useRef({
    // cache the previous title
    title: document.title,
  });

  const resize = useResizeObserver({
    onResize: () => {
      if (!rootElement) return;
      if (is.android) {
        // TODO: resolve android device rules
        // const nextHeight = window.innerHeight + pixels.android.locationBar.height;
        // if (rect.height !== nextHeight) {
        //   rootElement.style.height = `${nextHeight}px`;
        // }
      }
      // Scroll after a timeout, since iOS will scroll to the top of the page
      // after it fires the onload event
      setTimeout(scrollTo, 0, 0, 1);
    },
  });

  const onScroll = () => {
    // TODO: resolve android device rules
    // if (rootElement) {
    //   const nextHeight = window.innerHeight;
    //   rootElement.style.height = `${nextHeight}px`;
    // }
  };

  useEffect(() => {
    if (!rootElement) return;

    rootElement.style.position = 'fixed';
    rootElement.style.inset = '0';
    onScroll();

    if (is.android) {
      // Android's browser adds the scroll position to the innerHeight, just to
      // make this really difficult. Thus, once we are scrolled, the
      // page height value needs to be corrected in case the page is loaded
      // when already scrolled down. The pageYOffset is of no use, since it always
      // returns 0 while the address bar is displayed.
      window.addEventListener('scroll', onScroll);
    }

    resize.setElement(rootElement);
    return () => {
      if (is.android) {
        window.removeEventListener('scroll', onScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootElement]);

  useEffect(() => {
    // https://dev.to/oncode/display-your-pwa-website-fullscreen-4776
    // full screen for pwa ( on Android Chrome & iOS Safari )
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    window.matchMedia('(display-mode: standalone)').matches;
  }, []);

  useEffect(() => {
    document.title = title;
    const previousTitle = ref.current.title;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);


  return (
    <ErrorBoundary sectionId={sectionId}>{children}</ErrorBoundary>
  );
};

export { AppContainer, sectionId };
